import { create } from 'zustand';
import api from '../lib/api';
import type { ApprovedTeamsResponse, BlastSchool, BlastSport, BlastUser, ConvertRequest, ConvertResponse, EnrichedBlastTeamsResponse } from '../types';

interface IntegrationState {
  // Person blast status
  personBlastUser: BlastUser | null;
  isLoadingPersonStatus: boolean;
  personStatusError: string | null;

  // Org blast teams (enriched response with current/past split)
  orgBlastTeamsData: EnrichedBlastTeamsResponse | null;
  isLoadingTeams: boolean;
  teamsError: string | null;

  // Approved-teams option sets (for convert modal pre-filter)
  approvedTeamsData: ApprovedTeamsResponse | null;
  isLoadingApprovedTeams: boolean;
  approvedTeamsError: string | null;

  // Sports (for convert modal dropdown)
  blastSports: BlastSport[];
  isLoadingSports: boolean;

  // Convert
  isConverting: boolean;
  convertError: string | null;

  // Actions
  fetchBlastSports: () => Promise<void>;
  fetchPersonBlastStatus: (email: string) => Promise<void>;
  fetchOrgBlastTeams: (blastSchoolId: number) => Promise<void>;
  fetchApprovedTeams: (blastSchoolId: number) => Promise<void>;
  searchBlastSchools: (params: { name?: string; state?: string; limit?: number }) => Promise<BlastSchool[]>;
  fetchBlastUserByEmail: (email: string) => Promise<BlastUser | null>;
  convertPerson: (payload: ConvertRequest, idempotencyKey: string) => Promise<ConvertResponse>;
  clearPersonBlastStatus: () => void;
  clearOrgBlastTeams: () => void;
  clearApprovedTeams: () => void;
  clearConvertError: () => void;
}

export const useIntegrationStore = create<IntegrationState>((set) => ({
  // Initial state
  personBlastUser: null,
  isLoadingPersonStatus: false,
  personStatusError: null,

  orgBlastTeamsData: null,
  isLoadingTeams: false,
  teamsError: null,

  approvedTeamsData: null,
  isLoadingApprovedTeams: false,
  approvedTeamsError: null,

  blastSports: [],
  isLoadingSports: false,

  isConverting: false,
  convertError: null,

  fetchBlastSports: async () => {
    try {
      set({ isLoadingSports: true });
      const response = await api.get('/api/integration/sports');
      set({ blastSports: response.data as BlastSport[], isLoadingSports: false });
    } catch (error: any) {
      console.error('Failed to fetch Blast sports:', error);
      set({ isLoadingSports: false });
    }
  },

  fetchPersonBlastStatus: async (email: string) => {
    try {
      set({ isLoadingPersonStatus: true, personStatusError: null, personBlastUser: null });

      const params = new URLSearchParams({ email });
      const response = await api.get(`/api/integration/users?${params.toString()}`);

      set({
        personBlastUser: response.data as BlastUser,
        isLoadingPersonStatus: false,
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        set({ personBlastUser: null, isLoadingPersonStatus: false });
        return;
      }
      console.error('Failed to fetch Blast user status:', error);
      set({
        isLoadingPersonStatus: false,
        personStatusError: error.response?.data?.message || 'Failed to check Blast status',
      });
    }
  },

  fetchOrgBlastTeams: async (blastSchoolId: number) => {
    try {
      set({ isLoadingTeams: true, teamsError: null, orgBlastTeamsData: null });

      const params = new URLSearchParams({ school_id: String(blastSchoolId) });
      const response = await api.get(`/api/integration/teams?${params.toString()}`);

      set({
        orgBlastTeamsData: response.data as EnrichedBlastTeamsResponse,
        isLoadingTeams: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch Blast teams:', error);
      set({
        isLoadingTeams: false,
        teamsError: error.response?.data?.message || 'Failed to load Blast teams',
      });
    }
  },

  fetchApprovedTeams: async (blastSchoolId: number) => {
    try {
      set({ isLoadingApprovedTeams: true, approvedTeamsError: null, approvedTeamsData: null });

      const params = new URLSearchParams({ school_id: String(blastSchoolId) });
      const response = await api.get(`/api/integration/approved-teams?${params.toString()}`);

      set({
        approvedTeamsData: response.data as ApprovedTeamsResponse,
        isLoadingApprovedTeams: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch approved teams:', error);
      set({
        isLoadingApprovedTeams: false,
        approvedTeamsError: error.response?.data?.message || 'Failed to load approved teams',
      });
    }
  },

  searchBlastSchools: async (params) => {
    const searchParams = new URLSearchParams();
    if (params.name) searchParams.append('name', params.name);
    if (params.state) searchParams.append('state', params.state);
    if (params.limit) searchParams.append('limit', String(params.limit));

    const response = await api.get(`/api/integration/schools?${searchParams.toString()}`);
    return response.data as BlastSchool[];
  },

  fetchBlastUserByEmail: async (email: string) => {
    try {
      const params = new URLSearchParams({ email });
      const response = await api.get(`/api/integration/users?${params.toString()}`);
      return response.data as BlastUser;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      console.error('Failed to fetch Blast user by email:', error);
      return null;
    }
  },

  convertPerson: async (payload: ConvertRequest, idempotencyKey: string) => {
    try {
      set({ isConverting: true, convertError: null });

      const response = await api.post('/api/integration/convert', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      });

      set({ isConverting: false });
      return response.data as ConvertResponse;
    } catch (error: any) {
      const message = error.response?.data?.error?.message
        || error.response?.data?.message
        || 'Convert operation failed';
      set({ isConverting: false, convertError: message });
      throw error;
    }
  },

  clearPersonBlastStatus: () => {
    set({ personBlastUser: null, isLoadingPersonStatus: false, personStatusError: null });
  },

  clearOrgBlastTeams: () => {
    set({ orgBlastTeamsData: null, isLoadingTeams: false, teamsError: null });
  },

  clearApprovedTeams: () => {
    set({ approvedTeamsData: null, isLoadingApprovedTeams: false, approvedTeamsError: null });
  },

  clearConvertError: () => {
    set({ convertError: null });
  },
}));
